import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiFetch } from '../../lib/api';
import { useSession } from '../../lib/session';
import { Banner, Button, Card, Field, Spinner } from '../../components/ui';

interface Person {
  id: string;
  username: string | null;
  name: string;
  role: string;
  stores: { id: string; name: string }[];
  recentFailures: number;
  lockedOut: boolean;
}
interface Store {
  id: string;
  name: string;
}

export function People() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ username: '', name: '', password: '', role: 'user' });
  const [assigning, setAssigning] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const session = useSession();

  const people = useQuery({
    queryKey: ['people'],
    queryFn: () => apiFetch<Person[]>('/api/users'),
  });
  const stores = useQuery({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/api/stores'),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['people'] });
  };

  const create = useMutation({
    mutationFn: (body: unknown) =>
      apiFetch('/api/users', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: async () => {
      setForm({ username: '', name: '', password: '', role: 'user' });
      setError(null);
      await refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  const unlock = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/api/users/${userId}/unlock`, { method: 'POST', body: '{}' }),
    onSuccess: refresh,
    onError: (err: Error) => setError(err.message),
  });

  const assign = useMutation({
    mutationFn: (input: { storeId: string; userIds: string[] }) =>
      apiFetch(`/api/stores/${input.storeId}/admins`, {
        method: 'PUT',
        body: JSON.stringify({ userIds: input.userIds }),
      }),
    onSuccess: refresh,
    onError: (err: Error) => setError(err.message),
  });

  /**
   * Change someone's role.
   *
   * Without this the People screen was a one-way door: a store can only be
   * given to an account holding the admin role, and the role could only be
   * picked at the moment the login was created. Anyone made a "user" by mistake
   * could never be given a store — the only route was to delete them and start
   * again, taking their purchase history with it.
   */
  const setRole = useMutation({
    mutationFn: (input: { userId: string; role: string }) =>
      apiFetch(`/api/users/${input.userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role: input.role }),
      }),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => apiFetch(`/api/users/${userId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setConfirmingDelete(null);
      await refresh();
    },
    onError: (err: Error) => {
      setConfirmingDelete(null);
      setError(err.message);
    },
  });

  if (people.isPending) return <Spinner label="Loading people" />;

  return (
    <div>
      <h1 className="text-3xl text-ink-950">People</h1>
      <p className="mt-1.5 text-[14px] text-ink-500">
        Only you can issue logins. There is no public registration and no password-reset email — if
        someone is locked out, you unlock them here.
      </p>

      {error && (
        <div className="mt-5">
          <Banner tone="error">{error}</Banner>
        </div>
      )}

      <Card className="mt-6">
        <h2 className="sign text-[11px] text-ink-700">Create a login</h2>
        <form
          className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate({ ...form, username: form.username.trim().toLowerCase(), storeIds: [] });
          }}
        >
          <Field
            label="Username"
            required
            autoCapitalize="none"
            spellCheck={false}
            value={form.username}
            onChange={(event) => setForm((state) => ({ ...state, username: event.target.value }))}
          />
          <Field
            label="Display name"
            required
            value={form.name}
            onChange={(event) => setForm((state) => ({ ...state, name: event.target.value }))}
          />
          <Field
            label="Password"
            type="text"
            required
            minLength={12}
            hint="At least 12 characters"
            value={form.password}
            onChange={(event) => setForm((state) => ({ ...state, password: event.target.value }))}
          />
          <label className="block">
            <span className="sign block text-[11px] text-ink-700">Role</span>
            <select
              className="mt-1.5 w-full rounded-[3px] border border-paper-300 bg-paper-50 px-3 py-2 text-ink-900"
              value={form.role}
              onChange={(event) => setForm((state) => ({ ...state, role: event.target.value }))}
            >
              <option value="user">User — shops only</option>
              <option value="admin">Admin — runs assigned stores</option>
              <option value="owner">Owner — everything</option>
            </select>
          </label>
          <div className="sm:col-span-2 lg:col-span-4">
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create login'}
            </Button>
          </div>
        </form>
      </Card>

      <div className="mt-6 space-y-3">
        {people.data?.map((person) => (
          <div key={person.id} className="tag p-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="min-w-40">
                <p className="text-[15px] text-ink-950">{person.name}</p>
                <p className="tabular text-[12px] text-ink-500">@{person.username}</p>
              </div>

              {/* Editable in place. Demoting an admin also drops their store
                  assignments, which the server reports back. */}
              <select
                aria-label={`Role for ${person.name}`}
                className={`sign rounded-[2px] border-0 px-2 py-1 text-[9px] ${
                  person.role === 'owner'
                    ? 'bg-ink-900 text-paper-50'
                    : person.role === 'admin'
                      ? 'bg-leaf-300/50 text-leaf-700'
                      : 'bg-paper-200 text-ink-700'
                } ${person.id === session.data?.id ? 'opacity-60' : ''}`}
                value={person.role}
                disabled={person.id === session.data?.id || setRole.isPending}
                title={
                  person.id === session.data?.id
                    ? 'You cannot change your own role — ask another owner'
                    : 'Change role'
                }
                onChange={(event) =>
                  setRole.mutate({ userId: person.id, role: event.target.value })
                }
              >
                <option value="user">user</option>
                <option value="admin">admin</option>
                <option value="owner">owner</option>
              </select>

              {person.lockedOut && (
                <span className="sign text-[10px] text-tomato-600">
                  locked out ({person.recentFailures} failures)
                </span>
              )}

              {person.stores.length > 0 && (
                <span className="text-[13px] text-ink-500">
                  {person.stores.map((store) => store.name).join(', ')}
                </span>
              )}

              <div className="ml-auto flex flex-wrap gap-2">
                {person.role === 'admin' && (
                  <Button
                    variant="quiet"
                    onClick={() => setAssigning(assigning === person.id ? null : person.id)}
                  >
                    Stores
                  </Button>
                )}
                {person.lockedOut && (
                  <Button variant="quiet" onClick={() => unlock.mutate(person.id)}>
                    Unlock
                  </Button>
                )}
                {confirmingDelete === person.id ? (
                  <>
                    <Button
                      variant="danger"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(person.id)}
                    >
                      {remove.isPending ? 'Deleting…' : `Delete @${person.username}`}
                    </Button>
                    <Button variant="quiet" onClick={() => setConfirmingDelete(null)}>
                      Keep
                    </Button>
                  </>
                ) : (
                  <Button variant="danger" onClick={() => setConfirmingDelete(person.id)}>
                    Delete
                  </Button>
                )}
              </div>
            </div>

            {confirmingDelete === person.id && (
              <p className="mt-3 text-[13px] text-tomato-700">
                Deleting @{person.username} removes the login for good. Their past purchases stay in
                the shop&apos;s books but stop being attributed to anyone.
              </p>
            )}

            {/* Owners are deliberately excluded: they reach every store by role,
                so telling one to become an admin would be backwards. */}
            {person.role === 'user' && (
              <p className="mt-3 text-[12.5px] text-ink-500">
                Only admins can be given a store. Change the role to admin and a Stores button
                appears here.
              </p>
            )}

            {assigning === person.id && (
              <div className="mt-4 border-t hairline pt-4">
                <p className="sign text-[10px] text-ink-700">Assign to stores</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {stores.data?.map((store) => {
                    const on = person.stores.some((assigned) => assigned.id === store.id);
                    return (
                      <button
                        key={store.id}
                        type="button"
                        className={`sign rounded-[3px] border px-3 py-1.5 text-[11px] ${
                          on
                            ? 'border-leaf-700 bg-leaf-600 text-paper-50'
                            : 'border-paper-300 bg-paper-50 text-ink-700'
                        }`}
                        onClick={() => {
                          // Assignment is per store, so toggling one rewrites that
                          // store's whole admin list rather than this person's.
                          const current =
                            people.data
                              ?.filter((candidate) =>
                                candidate.stores.some((assigned) => assigned.id === store.id),
                              )
                              .map((candidate) => candidate.id) ?? [];
                          const next = on
                            ? current.filter((id) => id !== person.id)
                            : [...current, person.id];
                          assign.mutate({ storeId: store.id, userIds: next });
                        }}
                      >
                        {store.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
